"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

/** A modified click (new tab/window, or not the primary button) — left alone,
 *  since no in-page navigation follows it. */
function isModifiedClick(event: MouseEvent): boolean {
  return (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  );
}

/** A plain left-click on a same-origin link that actually changes page — not
 *  a modified click, a new-tab target, or a same-page hash jump. The hash
 *  case is the one we must leave alone: it's the hero "Scroll to explore"
 *  cue (`hero-scroll-cue.tsx`), the sole intentional use of smooth scroll. */
function isPageNavClick(event: MouseEvent): boolean {
  if (isModifiedClick(event)) return false;
  const anchor =
    event.target instanceof Element ? event.target.closest("a") : null;
  if (
    !anchor ||
    anchor.target === "_blank" ||
    anchor.origin !== location.origin
  ) {
    return false;
  }
  return anchor.pathname !== location.pathname || !anchor.hash;
}

/**
 * `tc-normalize.css` sets `scroll-behavior: smooth` on `<html>` globally, for
 * the hero "Scroll to explore" cue. Next's own scroll-to-top-on-navigate
 * inherits it too, so instead of landing at the top instantly it ANIMATES
 * over ~700ms — visibly sliding a listing page's cover section past the
 * content body right after it loads.
 *
 * Suppresses smooth scrolling for the duration of an internal navigation —
 * on the click or back/forward that STARTS it, before Next's own scroll call
 * can run — and restores it once the new route has actually mounted, so the
 * hero cue keeps its smooth, zero-JS scroll.
 *
 * The hero cue's own click is excluded above, but clicking it also fires a
 * `popstate` (same-document hash navigation does, not just real history
 * traversal) — so the `popstate` handler re-checks the pathname itself
 * rather than trusting "a popstate fired" to mean "the page changed."
 */
export function RouteScrollGuard(): null {
  const pathname = usePathname();
  const pathnameRef = useRef(pathname);

  useEffect(() => {
    const suppress = (): void => {
      document.documentElement.style.scrollBehavior = "auto";
    };
    const onClick = (event: MouseEvent): void => {
      if (isPageNavClick(event)) suppress();
    };
    const onPopState = (): void => {
      if (location.pathname !== pathnameRef.current) suppress();
    };
    document.addEventListener("click", onClick, { capture: true });
    window.addEventListener("popstate", onPopState);
    return () => {
      document.removeEventListener("click", onClick, { capture: true });
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  useEffect(() => {
    pathnameRef.current = pathname;
    const id = requestAnimationFrame(() => {
      document.documentElement.style.scrollBehavior = "";
    });
    return () => cancelAnimationFrame(id);
  }, [pathname]);

  return null;
}
